/**
 * Clients directory — the Clients tab under Projects. A plain list of
 * clients (accounts): search, add a client, and click a card to open the
 * account detail modal (Overview / Projects / Tasks / …). Project browsing
 * lives on the sibling Projects tab; this is purely the client roster.
 */

import { useEffect, useMemo, useState } from 'react';
import { Archive, Building2, ChevronRight, FolderKanban, ListTodo, Mail, Phone, Plus, Search, User, UserCog } from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { EmptyState, LoadingState } from '../components/States';
import { AccountDetailModal } from '../components/AccountDetailModal';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import { createAccount, subscribeAccounts } from '../lib/db/accounts';
import { subscribeProjects } from '../lib/db/projects';
import { subscribeTasks } from '../lib/db/tasks';
import type { Account, Project, ProjectTask } from '../lib/db/types';

const fieldStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 8,
  color: C.text,
  padding: '6px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
};

export function ClientsDirectoryView() {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Account | null>(null);

  useEffect(() => {
    let done = 0;
    const mark = () => {
      done += 1;
      if (done >= 3) setLoaded(true);
    };
    const unsubA = subscribeAccounts((r) => {
      setAccounts(r);
      mark();
    });
    const unsubP = subscribeProjects((r) => {
      setProjects(r);
      mark();
    });
    const unsubT = subscribeTasks((r) => {
      setTasks(r);
      mark();
    });
    return () => {
      unsubA();
      unsubP();
      unsubT();
    };
  }, []);

  // Per-client rollup: active + total projects, and open (not-done) tasks.
  const statsByClient = useMemo(() => {
    const m = new Map<string, { total: number; active: number; openTasks: number }>();
    const get = (id: string) => {
      let s = m.get(id);
      if (!s) {
        s = { total: 0, active: 0, openTasks: 0 };
        m.set(id, s);
      }
      return s;
    };
    for (const p of projects) {
      const s = get(p.clientId);
      s.total += 1;
      if (p.status === 'active') s.active += 1;
    }
    for (const t of tasks) {
      if (t.accountId && t.status !== 'done' && !t.archived) get(t.accountId).openTasks += 1;
    }
    return m;
  }, [projects, tasks]);

  const archivedCount = useMemo(() => accounts.filter((a) => a.archived).length, [accounts]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = accounts.filter((a) => (showArchived ? true : !a.archived));
    const list = q
      ? base.filter(
          (a) =>
            a.name.toLowerCase().includes(q) ||
            (a.pocName ?? '').toLowerCase().includes(q) ||
            (a.pocEmail ?? '').toLowerCase().includes(q),
        )
      : base;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts, search, showArchived]);

  const addClient = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await createAccount({ name });
      setNewName('');
      setShowNew(false);
      setSelected(created); // open the new client straight away
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) {
    return (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <LoadingState rows={4} label="Loading clients" />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: '0 0 auto' }}>
        {canMutate && (
          <button
            type="button"
            className="glass-btn glass-btn--primary"
            onClick={() => setShowNew((v) => !v)}
          >
            <Plus size={13} /> New client
          </button>
        )}
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
          <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: C.textDim }} />
          <input
            style={{ ...fieldStyle, paddingLeft: 26, width: '100%' }}
            placeholder="Search clients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {archivedCount > 0 && (
          <button
            type="button"
            className="glass-btn"
            onClick={() => setShowArchived((v) => !v)}
            title={showArchived ? 'Hide archived clients' : 'Show archived clients'}
            style={{ color: showArchived ? C.text : C.textMuted, whiteSpace: 'nowrap' }}
          >
            <Archive size={12} /> {showArchived ? 'Hide' : 'Show'} archived ({archivedCount})
          </button>
        )}
        <span style={{ fontSize: 11, color: C.textDim, whiteSpace: 'nowrap' }}>
          {visible.length} client{visible.length === 1 ? '' : 's'}
        </span>
      </div>

      {showNew && canMutate && (
        <GlassPanel variant="subtle" animate={false} style={{ padding: 12, display: 'flex', gap: 6, alignItems: 'center', flex: '0 0 auto' }}>
          <input
            style={{ ...fieldStyle, flex: 1 }}
            placeholder="New client name…"
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void addClient();
              }
              if (e.key === 'Escape') setShowNew(false);
            }}
          />
          <button
            type="button"
            className="glass-btn glass-btn--primary"
            onClick={() => void addClient()}
            disabled={!newName.trim() || busy}
            style={{ opacity: !newName.trim() || busy ? 0.5 : 1, whiteSpace: 'nowrap' }}
          >
            {busy ? 'Creating…' : 'Create client'}
          </button>
          <button type="button" className="glass-btn" onClick={() => setShowNew(false)} style={{ color: C.textMuted }}>
            Cancel
          </button>
        </GlassPanel>
      )}

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {visible.length === 0 ? (
          <EmptyState title="No clients yet" hint="Add your first client to start grouping projects and tasks." icon={<Building2 size={22} />} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {visible.map((a) => {
              const s = statsByClient.get(a.id) ?? { total: 0, active: 0, openTasks: 0 };
              const owner = a.assignedTo || a.ownerName;
              return (
                <GlassPanel
                  key={a.id}
                  variant="default"
                  animate={false}
                  onClick={() => setSelected(a)}
                  style={{ padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10 }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Building2 size={16} style={{ color: C.magentaSoft, flex: '0 0 auto' }} />
                      <span style={{ fontWeight: 700, fontSize: 15, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                      {a.archived && (
                        <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: C.textDim, border: '1px solid rgba(255,255,255,0.14)', borderRadius: 4, padding: '1px 5px', flex: '0 0 auto' }}>Archived</span>
                      )}
                    </span>
                    <ChevronRight size={14} style={{ color: C.textDim, flex: '0 0 auto' }} />
                  </div>

                  {/* Contact block */}
                  {(a.pocName || a.pocEmail || a.pocPhone) && (
                    <div style={{ display: 'grid', gap: 4, fontSize: 12, color: C.textMuted }}>
                      {a.pocName && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <User size={11} style={{ flex: '0 0 auto' }} /> {a.pocName}
                          {a.pocTitle && <span style={{ color: C.textDim }}>· {a.pocTitle}</span>}
                        </span>
                      )}
                      {a.pocEmail && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Mail size={11} style={{ flex: '0 0 auto' }} /> {a.pocEmail}
                        </span>
                      )}
                      {a.pocPhone && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Phone size={11} style={{ flex: '0 0 auto' }} /> {a.pocPhone}
                        </span>
                      )}
                    </div>
                  )}

                  {a.notes && (
                    <span style={{ fontSize: 12, color: C.textDim, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {a.notes}
                    </span>
                  )}

                  {/* Stat row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 'auto', paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 12, color: C.textMuted }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }} title="Active / total projects">
                      <FolderKanban size={12} style={{ color: C.magentaSoft }} />
                      <strong style={{ color: C.text }}>{s.active}</strong>/{s.total} project{s.total === 1 ? '' : 's'}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }} title="Open tasks">
                      <ListTodo size={12} style={{ color: s.openTasks > 0 ? C.orange : C.textDim }} />
                      <strong style={{ color: C.text }}>{s.openTasks}</strong> open
                    </span>
                    {owner && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title="Owner">
                        <UserCog size={12} /> {owner}
                      </span>
                    )}
                  </div>
                </GlassPanel>
              );
            })}
          </div>
        )}
      </div>

      {selected && <AccountDetailModal account={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
